import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TableContext } from '../../contexts/tableContext';
import { getCookies } from '../../hooks/useCookies';
import envValues from '../../enviornment';
import styles from './MultipleFileUpload.module.scss';

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

function parseMimeTypes(accept) {
  if (!accept) return [];
  return accept
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function mimeMatchesAccepted(fileMime, accepted) {
  const fm = (fileMime || '').toLowerCase();
  if (!fm) return false;
  return accepted.some((a) => {
    if (a === fm) return true;
    if ((a === 'image/jpg' || a === 'image/jpeg') && (fm === 'image/jpeg' || fm === 'image/jpg'))
      return true;
    return false;
  });
}

function extFromName(name) {
  const i = name.lastIndexOf('.');
  if (i === -1) return '';
  return name.slice(i + 1).toLowerCase();
}

function getAllowedExtensions(accept) {
  const types = parseMimeTypes(accept);
  const exts = new Set();
  types.forEach((t) => {
    if (t === 'image/png') exts.add('png');
    if (t === 'image/jpeg' || t === 'image/jpg') {
      exts.add('jpg');
      exts.add('jpeg');
    }
    if (t === 'image/gif') exts.add('gif');
    if (t === 'image/svg+xml') exts.add('svg');
  });
  return exts;
}

function validateFile(file, accept, maxBytes) {
  if (file.size > maxBytes) {
    const mb = maxBytes / (1024 * 1024);
    const sizeText =
      mb >= 1
        ? `${Number.isInteger(mb) ? mb : parseFloat(mb.toFixed(1))} MB`
        : `${Math.ceil(maxBytes / 1024)} KB`;
    return { ok: false, message: `File is too large. Maximum size is ${sizeText}.` };
  }
  if (!accept) return { ok: true };
  const allowedMimes = parseMimeTypes(accept);
  const allowedExts = getAllowedExtensions(accept);
  const ext = extFromName(file.name);
  const typeOk = mimeMatchesAccepted(file.type, allowedMimes);
  const extOk = ext && allowedExts.has(ext);
  if (typeOk || extOk) return { ok: true };
  return {
    ok: false,
    message: 'Invalid file type. Choose a file that matches the allowed formats listed above.',
  };
}

function MultipleFileUpload({
  allowMultiple,
  editFiles,
  section,
  accept,
  format,
  setUpdatedFile,
  onFilesChange,
  uploadId,
  maxFileSizeBytes,
  largePreview,
}) {
  const { formthemeStyle } = useContext(TableContext);
  const [edited, setEdited] = useState(editFiles);
  const [uploadedImgs, setUplodedImgs] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const token = `Bearer ${getCookies('Token')}`;
  const params = useParams();
  const { userId } = params;
  const pdfImg =
    'https://thesoftwarepro.com/wp-content/uploads/2019/12/microsoft-office-pdf-document-953x1024.jpg';
  const videoImg =
    'https://image.shutterstock.com/image-vector/play-button-icon-vector-illustration-260nw-1697833306.jpg';

  useEffect(() => {
    setEdited(editFiles);
  }, [editFiles]);

  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(selectedFiles);
    }
  }, [selectedFiles, onFilesChange]);

  const handleChange = async (e) => {
    const { files } = e.target;
    const inputEl = e.target;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const imgUrl = [];
    const validFiles = [];

    for (let i = 0; i < fileArray.length; i += 1) {
      const file = fileArray[i];
      const { ok, message } = validateFile(file, accept, maxFileSizeBytes);
      if (!ok) {
        setFileError(message);
        inputEl.value = '';
        return;
      }
      validFiles.push(file);
      const extension = extFromName(file.name);
      if (extension === 'mp4' || extension === 'mov') {
        imgUrl.push({ imgurl: URL.createObjectURL(file), extension: 'video' });
      } else if (extension === 'pdf') {
        imgUrl.push({ imgurl: URL.createObjectURL(file), extension: 'pdf' });
      } else {
        imgUrl.push({ imgurl: URL.createObjectURL(file), extension: 'image' });
      }
    }

    setFileError('');
    setUplodedImgs((prev) => [...prev, ...imgUrl]);
    setSelectedFiles((prev) => [...prev, ...validFiles]);

    if (setUpdatedFile) {
      setUpdatedFile(true);
    }
    inputEl.value = '';
  };

  const handleCancelButton = (param) => () => {
    const updatedImgs = uploadedImgs.filter((value, index) => index !== param);
    const updatedFiles = selectedFiles.filter((value, index) => index !== param);

    setUplodedImgs(updatedImgs);
    setSelectedFiles(updatedFiles);
    setFileError('');

    if (updatedFiles.length === 0 && setUpdatedFile) {
      setUpdatedFile(false);
    }
  };

  const handleRemoveButton = (param) => () => {
    axios
      .delete(`${envValues.REACT_APP_API_ENDPOINT}/${section}/${userId}/${param}`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        setEdited(res.data.data.user.files);
        toast.success('File removed!');
        setUpdatedFile(true);
      });
  };

  const rootClass = largePreview
    ? `${styles[formthemeStyle]} form-group ${styles.largePreviewRoot}`
    : `${styles[formthemeStyle]} form-group`;

  return (
    <div className={rootClass}>
      <div className="d-flex">
        <div className="d-flex">
          <div className="file-uploader-mask d-flex justify-content-center align-items-center" />
          <input
            multiple={allowMultiple}
            className="file-input"
            id={uploadId || 'upload'}
            type="file"
            onChange={handleChange}
            accept={accept}
            hidden
          />
          <label htmlFor={uploadId || 'upload'} className={styles.upload_btn}>
            Choose file
          </label>
        </div>
      </div>
      {format && (
        <span className={styles.formatHint}>
          {format}
          {maxFileSizeBytes ? (
            <span className={styles.maxSizeHint}>
              {' '}
              · Max {(maxFileSizeBytes / (1024 * 1024)).toFixed(0)} MB per file
            </span>
          ) : null}
        </span>
      )}
      {fileError ? <div className={styles.fileError}>{fileError}</div> : null}
      <div className="d-flex flex-wrap mt-4">
        {edited != null
          ? edited.map((uploadedImg, index) => (
              <div className={styles.close_icon_main} key={uploadedImg.path}>
                <img
                  src={uploadedImg.path}
                  style={{ height: 100, width: 100, objectFit: 'contain' }}
                  alt="UploadedImages"
                  onError={(errEv) => {
                    errEv.target.onerror = null;
                    errEv.target.src = uploadedImg.extension === 'video' ? videoImg : pdfImg;
                  }}
                  className="img-thumbnail img-fluid uploaded-img mr-5"
                />
                <button
                  type="button"
                  className={styles.close_icon}
                  onClick={handleRemoveButton(index)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 19 19"
                    stroke="#60677e"
                    strokeWidth="2"
                    className="react-date-picker__clear-button__icon react-date-picker__button__icon"
                    style={{ marginTop: '-10px', marginLeft: '-3px' }}
                  >
                    <line x1="4" x2="15" y1="4" y2="15" />
                    <line x1="15" x2="4" y1="4" y2="15" />
                  </svg>
                </button>
              </div>
            ))
          : null}
      </div>
      <div className="image upload">
        {uploadedImgs != null
          ? uploadedImgs.map((uploadedImg, index) => (
              <div key={uploadedImg.imgurl} className={styles.previewBlock}>
                <div
                  className={
                    largePreview
                      ? `${styles.uploaded_imgsection} ${styles.uploaded_imgsectionLarge}`
                      : styles.uploaded_imgsection
                  }
                >
                  <img
                    src={uploadedImg.imgurl}
                    alt="UploadedImages"
                    onError={(errEv) => {
                      errEv.target.onerror = null;
                      errEv.target.src = uploadedImg.extension === 'video' ? videoImg : pdfImg;
                    }}
                    className={`img-thumbnail img-fluid uploaded-img mr-2 ${
                      largePreview ? styles.previewImgLarge : ''
                    }`}
                    id={styles.uploaded_img}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCancelButton(index)}
                  className={styles.imgcancel_btn}
                >
                  Remove
                </button>
              </div>
            ))
          : null}
      </div>
    </div>
  );
}

MultipleFileUpload.propTypes = {
  allowMultiple: PropTypes.bool,
  editFiles: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
  section: PropTypes.string,
  accept: PropTypes.string,
  format: PropTypes.string,
  setUpdatedFile: PropTypes.func,
  onFilesChange: PropTypes.func,
  uploadId: PropTypes.string,
  maxFileSizeBytes: PropTypes.number,
  largePreview: PropTypes.bool,
};
MultipleFileUpload.defaultProps = {
  allowMultiple: true,
  editFiles: null,
  section: 'user',
  accept: 'image/png, image/jpeg',
  format: '*.jpg,*.png,*.jpeg',
  setUpdatedFile: null,
  onFilesChange: null,
  uploadId: 'upload',
  maxFileSizeBytes: DEFAULT_MAX_BYTES,
  largePreview: false,
};

export default MultipleFileUpload;
